"use client";
import { Children } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCallback } from "react";
import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Circle } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { usePrevNextButtons } from "./CarouselArrowButtons";
import { useDotButton } from "./CarouselDotButton";
import { getEmblaSlideAccessibilityProps } from "@/components/Carousel/emblaSlideAccessibility";
import { useCarouselMessages } from "@/i18n/hooks/useCarouselMessages";
import "./styles.css";

const options: EmblaOptionsType = { loop: true };
export type CarouselProps = {
  page_title?: string;
  autoplay_enabled?: "true" | "false";
  autoplay_delay?: string;
  children?: React.ReactNode[];
};

// Child component for carousel items - exact props as specified
export type CarouselItemProps = React.PropsWithChildren<{
  // Basic content
  title?: string;
  title_text_color?: string;
  description_item?: string;
  description_text_color?: string;
  theme?: "light" | "dark";

  // Images
  image_url_desktop?: string;
  image_url_tablet?: string;
  image_url_mobile?: string;

  // CTAs
  primary_cta_text?: string;
  primary_cta_link?: string;
  secondary_cta_text?: string;
  secondary_cta_link?: string;

  // Testimonial
  testimonial_quote?: string;
  testimonial_author?: string;
  testimonial_text_color?: string;

  // Stat
  stat_icon?: string;
  stat_value?: string;
  stat_label?: string;
  stat_source_name?: string;
  stat_source_link?: string;
  stat_text_color?: string;

  // Product card list
  product_card_list?: string;

  // Tooltip
  tooltip_label?: string;
  tooltip_content?: string;
  tooltip_enabled?: string;

  // Search
  search_placeholder?: string;
  search_button_color?: string;
  search_button_label?: string;
  search_button_label_color?: string;
}>;

export default function ShortCodeCarousel({
  page_title = "Build new skills. Advance your career.",
  autoplay_enabled = "false",
  autoplay_delay = "8000",
  children,
}: CarouselProps) {
  const carouselMessages = useCarouselMessages();

  const plugins =
    autoplay_enabled === "true"
      ? [Autoplay({ delay: Number(autoplay_delay) })]
      : [];

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    autoplay.reset();
  }, []);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick,
  );

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onNavButtonClick);

  return (
    <div className="embla mx-break-out relative">
      <h1 className="sr-only">{page_title}</h1>

      {/* Slides */}
      <div
        className="embla__viewport z-10"
        ref={emblaRef}
        role="region"
        aria-label={carouselMessages.homepageHeroCarousel}
        aria-roledescription="carousel"
      >
        <div className="embla__container flex">
          {Children.map(children, (child, index) => {
            const isVisible = index === selectedIndex;

            return (
              <div
                key={index}
                className="embla__slide"
                aria-label={carouselMessages.slideLabel(index + 1)}
                aria-roledescription="slide"
                {...getEmblaSlideAccessibilityProps(isVisible)}
              >
                {child}
              </div>
            );
          })}
        </div>
      </div>

      {/* Controls */}
      <div className="absolute w-full bottom-0 p-12">
        <div className="w-full flex justify-center">
          <div className="flex items-center bg-white gap-2 rounded-full">
            <Button
              variant="link"
              size="icon"
              onClick={onPrevButtonClick}
              className="text-primary hover:text-brand"
              disabled={prevBtnDisabled}
            >
              <ChevronLeft svgProps={{ width: 40, height: 40 }} />
              <span className="sr-only">
                {carouselMessages.goToPreviousSlide}
              </span>
            </Button>
            {scrollSnaps.map((_, index) => (
              <Button
                key={uuidv4()}
                variant="link"
                size="icon"
                onClick={() => onDotButtonClick(index)}
                className={`hover:text-brand ${index === selectedIndex ? "text-brand" : "text-primary"}`}
              >
                <Circle svgProps={{ width: 14, height: 14 }} />
                <span className="sr-only">
                  {carouselMessages.goToSlide(index + 1)}
                </span>
              </Button>
            ))}
            <Button
              variant="link"
              size="icon"
              onClick={onNextButtonClick}
              className="text-primary hover:text-brand"
              disabled={nextBtnDisabled}
            >
              <ChevronRight svgProps={{ width: 40, height: 40 }} />
              <span className="sr-only">{carouselMessages.goToNextSlide}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
